import React from 'react';
import useAutoFocusFirstField from 'hooks/useAutoFocusFirstField';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import { Button, TextField, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import ConfirmDialog from 'components/ConfirmDialog';
import SaveError from 'components/common/SaveError';
import ImageInput from 'components/ImageInput';
import SelectField from 'components/SelectField';
import { GlobalProductFormSchema } from 'forms/schemas/StockModuleForm';
import { GlobalProduct, StockProductType, UnitOfMeasure } from 'types/models/StockReceipt';
import Venue from 'types/models/Venue';
import { createGlobalProduct, updateGlobalProduct } from 'services/stockServices';
import { getVenuesWithStockModuleActive } from 'services/venuesServices';
import { mapToSelectOptions } from 'utils/select';
import { getApiErrorMessage } from 'utils/errors';
import AppContext from 'contexts/AppContext';
import useSetupInitialState from 'hooks/useSetupInitialState';
import UserRoleType from 'types/enums/UserRoleType';

type Props = {
  globalProduct?: GlobalProduct;
};

type FormValues = {
  name: string;
  sku: string;
  venueId: string;
  type: StockProductType;
  unitMeasure: UnitOfMeasure | '';
  receptionUnitMeasure: UnitOfMeasure | '';
  unitsPerReceptionUnit: number | '';
  minimumStock: number | '';
  safetyStock: number | '';
  replacementCost: number | '';
  image: File | null;
};

export default ({ globalProduct }: Props): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();

  const isCreate = !globalProduct;

  const { authData } = React.useContext(AppContext);
  const entity = useSetupInitialState();
  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;

  const [venues, setVenues] = React.useState<Venue[]>([]);
  const [isLoadingVenues, setIsLoadingVenues] = React.useState(false);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const formRef = useAutoFocusFirstField(true);

  const typeOptions = [
    { value: StockProductType.FINISHED, label: t('stock.global-products.type-finished') },
    { value: StockProductType.RAW_MATERIAL, label: t('stock.global-products.type-raw-material') },
  ];

  const unitMeasureOptions = Object.values(UnitOfMeasure).map((unit) => ({
    value: unit,
    label: t(`stock.units.${unit}`),
  }));

  React.useEffect(() => {
    if (!isCreate) return;
    if (isVenueEditor) return;

    let mounted = true;

    async function loadVenues() {
      setIsLoadingVenues(true);

      try {
        const response = await getVenuesWithStockModuleActive();

        if (!mounted) return;

        setVenues(response?.data ?? []);
      } catch (error) {
        console.error(error);
      } finally {
        if (mounted) {
          setIsLoadingVenues(false);
        }
      }
    }

    loadVenues();

    return () => {
      mounted = false;
    };
  }, [isCreate, isVenueEditor]);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      name: globalProduct?.name ?? '',
      sku: globalProduct?.sku ?? '',
      venueId: globalProduct?.venueId ?? entity?.id ?? '',
      type: globalProduct?.type ?? StockProductType.FINISHED,
      unitMeasure: globalProduct?.unitMeasure ?? '',
      receptionUnitMeasure: globalProduct?.receptionUnitMeasure ?? (isCreate ? UnitOfMeasure.UNIT : ''),
      unitsPerReceptionUnit: globalProduct?.unitsPerReceptionUnit ?? 1,
      minimumStock: globalProduct?.minimumStock ?? '',
      safetyStock: globalProduct?.safetyStock ?? '',
      replacementCost: globalProduct?.replacementCost ?? '',
      image: null,
    },
    validationSchema: () => GlobalProductFormSchema(t, isCreate),
    onSubmit: () => setOpenDialog(true),
  });

  const effectiveType = formik.values.type;
  const showUnitMeasure = effectiveType === StockProductType.RAW_MATERIAL;

  // Las unidades de medida no pueden modificarse si el producto está en una receta o posee stock
  // (solo aplica en edición; el backend lo informa en isUnitsLocked).
  const unitsLocked = !isCreate && Boolean(globalProduct?.isUnitsLocked);

  async function onConfirm() {
    setSaveError(null);

    const formData = new FormData();
    formData.append('name', formik.values.name);
    formData.append('minimumStock', formik.values.minimumStock === '' ? '' : String(formik.values.minimumStock));
    formData.append('safetyStock', formik.values.safetyStock === '' ? '' : String(formik.values.safetyStock));
    if (formik.values.image) {
      formData.append('image', formik.values.image as unknown as File);
    }
    formData.append('type', formik.values.type);
    formData.append('unitMeasure', showUnitMeasure ? formik.values.unitMeasure : '');
    // Unidad de recepción: default UNIT. Factor de conversión: default 1.
    formData.append('receptionUnitMeasure', formik.values.receptionUnitMeasure || UnitOfMeasure.UNIT);
    formData.append(
      'unitsPerReceptionUnit',
      formik.values.unitsPerReceptionUnit === '' ? '' : String(formik.values.unitsPerReceptionUnit),
    );
    if (isCreate) {
      formData.append('sku', formik.values.sku);
      formData.append('venueId', formik.values.venueId);
      formData.append(
        'replacementCost',
        formik.values.replacementCost === '' ? '' : String(formik.values.replacementCost),
      );
    }

    try {
      if (isCreate) {
        await createGlobalProduct(formData);
      } else if (globalProduct) {
        await updateGlobalProduct(globalProduct.id, formData);
      }
      history.go(-1);
    } catch (error) {
      setSaveError(getApiErrorMessage(error) ?? t('errors.generic'));
    } finally {
      formik.setSubmitting(false);
    }
  }

  return (
    <>
      <ConfirmDialog
        open={openDialog}
        setOpen={setOpenDialog}
        onConfirm={onConfirm}
        onCancel={() => formik.setSubmitting(false)}
      />

      <form onSubmit={formik.handleSubmit} ref={formRef}>
        <h2>{isCreate ? t('stock.global-products.form.create-title') : t('stock.global-products.form.title')}</h2>

        {isCreate && !isVenueEditor && (
          <SelectField
            name="venueId"
            label={t('stock.forms.venue')}
            placeholder={t('stock.global-products.form.venue-placeholder')}
            value={formik.values.venueId}
            options={mapToSelectOptions(venues, { label: 'name' })}
            setFieldValue={formik.setFieldValue}
            touched={formik.touched.venueId}
            error={formik.errors.venueId}
            isMulti={false}
            isSearchable
            isClearable={false}
            backspaceRemovesValue={false}
            onBlur={formik.handleBlur}
            isDisabled={isLoadingVenues}
          />
        )}
        {isCreate && (
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            {t('stock.global-products.form.save-to-central')}
          </Typography>
        )}
        <SelectField
          name="type"
          label={t('stock.global-products.form.type')}
          placeholder={t('stock.global-products.form.type-placeholder')}
          value={formik.values.type}
          options={typeOptions}
          setFieldValue={(name, value) => {
            if (value === StockProductType.FINISHED) {
              formik.setFieldValue('unitMeasure', '');
            }
            formik.setFieldValue(name, value);
          }}
          touched={formik.touched.type}
          error={formik.errors.type}
          isMulti={false}
          isSearchable
          isClearable={false}
          backspaceRemovesValue={false}
          onBlur={formik.handleBlur}
          isDisabled={formik.isSubmitting}
        />

        {showUnitMeasure && (
          <SelectField
            name="unitMeasure"
            label={t('stock.global-products.form.unit-measure')}
            placeholder={t('stock.global-products.form.unit-measure-placeholder')}
            value={formik.values.unitMeasure}
            options={unitMeasureOptions}
            setFieldValue={formik.setFieldValue}
            touched={formik.touched.unitMeasure}
            error={formik.errors.unitMeasure}
            isMulti={false}
            isSearchable
            isClearable
            backspaceRemovesValue
            onBlur={formik.handleBlur}
            isDisabled={formik.isSubmitting || unitsLocked}
            helperText={t('stock.global-products.form.unit-measure-helper')}
          />
        )}

        <SelectField
          name="receptionUnitMeasure"
          label={t('stock.global-products.form.reception-unit-measure')}
          placeholder={t('stock.global-products.form.reception-unit-measure-placeholder')}
          value={formik.values.receptionUnitMeasure || UnitOfMeasure.UNIT}
          options={unitMeasureOptions}
          setFieldValue={formik.setFieldValue}
          touched={formik.touched.receptionUnitMeasure}
          error={formik.errors.receptionUnitMeasure}
          isMulti={false}
          isSearchable
          isClearable={false}
          backspaceRemovesValue
          onBlur={formik.handleBlur}
          // Los productos terminados solo se reciben/cuentan por unidades (fijas).
          isDisabled={formik.isSubmitting || unitsLocked || effectiveType !== StockProductType.RAW_MATERIAL}
          helperText={t('stock.global-products.form.reception-unit-measure-helper')}
        />

        {showUnitMeasure && (
          <TextField
            fullWidth
            margin="normal"
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            label={t('stock.global-products.form.units-per-reception-unit')}
            name="unitsPerReceptionUnit"
            value={formik.values.unitsPerReceptionUnit}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.unitsPerReceptionUnit && Boolean(formik.errors.unitsPerReceptionUnit)}
            helperText={
              formik.touched.unitsPerReceptionUnit && formik.errors.unitsPerReceptionUnit
                ? formik.errors.unitsPerReceptionUnit
                : t('stock.global-products.form.units-per-reception-unit-helper')
            }
            disabled={formik.isSubmitting || unitsLocked}
          />
        )}

        <TextField
          fullWidth
          margin="normal"
          label={t('stock.global-products.form.name')}
          name="name"
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          disabled={formik.isSubmitting}
        />

        <TextField
          fullWidth
          margin="normal"
          label={t('stock.global-products.form.sku')}
          name="sku"
          value={formik.values.sku}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.sku && Boolean(formik.errors.sku)}
          helperText={formik.touched.sku && formik.errors.sku}
          disabled={formik.isSubmitting || !isCreate}
          InputProps={!isCreate ? { readOnly: true } : undefined}
        />

        <ImageInput
          className="btn-50"
          label={`${
            formik.values.image || globalProduct?.image
              ? t('stock.global-products.form.image-edit')
              : t('stock.global-products.form.image-create')
          } `}
          name="image"
          isSubmitting={formik.isSubmitting}
          value={formik.values.image}
          setFieldValue={formik.setFieldValue}
          touched={formik.touched.image}
          errors={formik.errors.image}
          preview={globalProduct?.image ?? undefined}
        />

        <TextField
          fullWidth
          margin="normal"
          type="number"
          inputProps={{ min: 0 }}
          label={t('stock.global-products.form.minimum-stock')}
          name="minimumStock"
          value={formik.values.minimumStock}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.minimumStock && Boolean(formik.errors.minimumStock)}
          helperText={formik.touched.minimumStock && formik.errors.minimumStock}
          disabled={formik.isSubmitting}
        />

        <TextField
          fullWidth
          margin="normal"
          type="number"
          inputProps={{ min: 0 }}
          label={t('stock.global-products.form.safety-stock')}
          name="safetyStock"
          value={formik.values.safetyStock}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.safetyStock && Boolean(formik.errors.safetyStock)}
          helperText={formik.touched.safetyStock && formik.errors.safetyStock}
          disabled={formik.isSubmitting}
        />

        <TextField
          fullWidth
          margin="normal"
          type="number"
          inputProps={{ min: 0, step: 0.01 }}
          label={t('stock.global-products.replacement-cost')}
          name="replacementCost"
          value={formik.values.replacementCost}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.replacementCost && Boolean(formik.errors.replacementCost)}
          helperText={formik.touched.replacementCost && formik.errors.replacementCost}
          disabled={formik.isSubmitting || !isCreate}
          InputProps={!isCreate ? { readOnly: true } : undefined}
        />

        <SaveError error={saveError} />

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 16,
            marginTop: 24,
          }}
        >
          <Button color="primary" variant="contained" type="submit" disabled={formik.isSubmitting}>
            {isCreate ? t('stock.global-products.form.save') : t('stock.global-products.form.save-changes')}
          </Button>
        </div>
      </form>
    </>
  );
};
